import {adminPermissions, opportunityPermissions, permissions} from '@/auth';
import {AchievementsTab, ActivitiesTab, BadgesTab, HistoryTab} from '@/components/geek-profile';
import {
  AccessDeniedView,
  AccountView,
  AchievementsAddTab,
  AchievementsGrantTab,
  AchievementsHistoryTab,
  AchievementsManagementView,
  AchievementTypesTab,
  ActivitiesHistoryTab,
  ActivitiesManagementView,
  AddActivityTab,
  AddGCoinsTab,
  AdminView,
  AuthenticationServerNotRespondingView,
  ChangeEmailView,
  ConfirmEmailView,
  DashboardView,
  ErrorView,
  ExchangeGCoinsTab,
  ExportWithdrawalsTab,
  FeedbackView,
  ForgotPasswordView,
  GCoinsHistoryTab,
  GCoinsManagementView,
  GCoinsView,
  GeekProfileView,
  GeeksBalanceTab,
  GeeksManagementView,
  KitchenTvView,
  LeaderboardView,
  LoginView,
  MyAccountView,
  NewsEditView,
  NewsManagementView,
  OpportunityCancelationView,
  OpportunityDetailsView,
  OpportunityDraftDetailsView,
  OpportunityDraftEditView,
  OpportunityEditView,
  OpportunityFinalizationView,
  OpportunityNewView,
  OpportunityPermissionsView,
  OpportunityRootView,
  OpportunityView,
  RegisterAccountView,
  ResetPasswordView,
  RewardGeekView,
  RulesView,
  SeasonsManagementView,
  SignOutView,
  WaitingActivitiesTab,
  WaitingRequestsTab,
  WaitingWithdrawalsTab,
} from '@/views';
import {RouteConfig} from 'vue-router';
import {RootStore} from '@/store';
import {router} from '@/main';
import {flatPermissions} from '@/shared';

export const ROUTES_PL: RouteConfig[] = [
  {
    path: '/',
    redirect: 'home',
  },
  {
    path: '/home',
    name: 'home',
    component: DashboardView,
    meta: { requiredPermissions: permissions.home },
  },
  {
    path: '/opportunities',
    component: OpportunityRootView,
    meta: { requiredPermissions: permissions.opportunity },
    children: [
      {
        path: '',
        name: 'opportunity',
        component: OpportunityView,
      },
      {
        path: 'new',
        name: 'opportunity-new',
        component: OpportunityNewView,
        meta: { requiredPermissions: opportunityPermissions.addNewOpportunity },
      },
      {
        path: 'permissions',
        name: 'opportunity-permissions',
        component: OpportunityPermissionsView,
        meta: { requiredPermissions: opportunityPermissions.grantRoles },
      },
      {
        path: ':id',
        name: 'opportunity-details',
        component: OpportunityDetailsView,
        props: true,
      },
      {
        path: ':id/cancelation',
        name: 'opportunity-cancelation',
        component: OpportunityCancelationView,
      },
      {
        path: ':id/finalization',
        name: 'opportunity-finalization',
        component: OpportunityFinalizationView,
      },
      {
        path: ':id/edit',
        name: 'opportunity-edit',
        component: OpportunityEditView,
        meta: { requiredPermissions: opportunityPermissions.addNewOpportunity },
      },
      {
        path: 'drafts/:id',
        name: 'opportunity-draft-details',
        component: OpportunityDraftDetailsView,
        props: true,
      },
      {
        path: 'drafts/:id/edit',
        name: 'opportunity-draft-edit',
        component: OpportunityDraftEditView,
        meta: { requiredPermissions: opportunityPermissions.addNewOpportunity },
      },
    ],
  },
  {
    path: '/rules',
    name: 'rules',
    component: RulesView,
    meta: { requiredPermissions: permissions.rules },
  },
  {
    path: '/reward-geek',
    name: 'reward-geek',
    component: RewardGeekView,
    meta: { requiredPermissions: permissions.rewardGeek },
  },
  {
    path: '/geek-profile',
    name: 'your-geek-profile',
    beforeEnter: () => {
      router.push({name: 'geek-profile', params: {geekId: RootStore.state.geek.current.id}});
    },
  },
  {
    path: '/geek-profile/:geekId',
    component: GeekProfileView,
    meta: { requiredPermissions: permissions.geekProfile },
    children: [
      {
        path: '',
        name: 'geek-profile',
        component: ActivitiesTab,
        redirect: { name: 'geek-activities' },
      },
      {
        path: 'activities',
        name: 'geek-activities',
        component: ActivitiesTab,
      },
      {
        path: 'history',
        name: 'geek-history',
        component: HistoryTab,
      },
      {
        path: 'badges',
        name: 'geek-badges',
        component: BadgesTab,
      },
      {
        path: 'achievements',
        name: 'geek-achievements',
        component: AchievementsTab,
      },
    ],
  },
  {
    path: '/leaderboard',
    name: 'leaderboard',
    meta: { requiredPermissions: permissions.leaderboard },
    component: LeaderboardView,
  },
  {
    path: '/admin-panel',
    component: AdminView,
    meta: { requiredOneOfPermissions: flatPermissions(adminPermissions) },
    children: [
      {
        path: '/',
        name: 'admin-panel',
        redirect: { name: 'admin-activities-waiting-requests' },
      },
      {
        path: 'gcoins',
        component: GCoinsManagementView,
        meta: { requiredPermissions: adminPermissions.gCoins },
        children: [
          {
            path: '',
            name: 'admin-gcoins',
            component: HistoryTab,
            redirect: { name: 'admin-gcoins-waiting-requests' },
          },
          {
            path: 'waiting-requests',
            name: 'admin-gcoins-waiting-requests',
            component: WaitingRequestsTab,
          },
          {
            path: 'waiting-withdrawals',
            name: 'admin-gcoins-waiting-withdrawals',
            component: WaitingWithdrawalsTab,
          },
          {
            path: 'export-withdrawals',
            name: 'admin-gcoins-export-withdrawals',
            component: ExportWithdrawalsTab,
          },
          {
            path: 'history',
            name: 'admin-gcoins-history',
            component: GCoinsHistoryTab,
          },
          {
            path: 'balance',
            name: 'admin-gcoins-balance',
            component: GeeksBalanceTab,
          },
          {
            path: 'exchange',
            name: 'admin-gcoins-exchange',
            component: ExchangeGCoinsTab,
          },
          {
            path: 'add',
            name: 'admin-gcoins-add',
            component: AddGCoinsTab,
          },
        ],
      },
      {
        path: 'achievements',
        component: AchievementsManagementView,
        meta: { requiredPermissions: adminPermissions.achievements },
        children: [
          {
            path: 'history',
            name: 'admin-achievements-history',
            component: AchievementsHistoryTab,
          },
          {
            path: 'achievement-types',
            name: 'admin-achievements-types',
            component: AchievementTypesTab,
          },
          {
            path: 'grant',
            name: 'admin-achievements-grant',
            component: AchievementsGrantTab,
          },
          {
            path: 'add',
            name: 'admin-achievements-add',
            component: AchievementsAddTab,
          },
        ],
      },
      {
        path: 'activities',
        component: ActivitiesManagementView,
        meta: { requiredPermissions: adminPermissions.activities },
        children: [
          {
            path: '',
            name: 'admin-activities',
            component: WaitingActivitiesTab,
            redirect: { name: 'admin-activities-waiting-requests' },
          },
          {
            path: 'history',
            name: 'admin-activities-history',
            component: ActivitiesHistoryTab,
          },
          {
            path: 'waiting-requests',
            name: 'admin-activities-waiting-requests',
            component: WaitingActivitiesTab,
          },
          {
            path: 'add',
            name: 'admin-activities-add',
            component: AddActivityTab,
          },
        ],
      },
      {
        path: 'geeks',
        name: 'admin-geeks',
        component: GeeksManagementView,
        meta: { requiredPermissions: adminPermissions.geeks },
      },
      {
        path: 'seasons',
        name: 'admin-seasons',
        component: SeasonsManagementView,
        meta: { requiredPermissions: adminPermissions.seasons },
      },
      {
        path: 'news',
        name: 'admin-news',
        component: NewsManagementView,
        meta: { requiredPermissions: adminPermissions.news },
      },
      {
        path: 'news/edit',
        name: 'news-edit',
        component: NewsEditView,
        meta: { requiredPermissions: adminPermissions.news },
      },
    ],
  },
  {
    path: '/feedback',
    name: 'feedback',
    component: FeedbackView,
    meta: { requiredPermissions: permissions.feedback },
  },
  {
    path: '/gcoins',
    name: 'gcoins',
    component: GCoinsView,
    meta: { requiredPermissions: permissions.gCoins },
  },
  {
    path: '/tv',
    name: 'kitchen-tv',
    component: KitchenTvView,
  },
  {
    path: '/account',
    component: AccountView,
    children: [
      {
        path: '',
        component: MyAccountView,
        name: 'account',
        meta: { requiredPermissions: permissions.myAccount },
      },
      {
        path: 'sign-in',
        name: 'account-sign-in',
        component: LoginView,
      },
      {
        path: 'sign-out',
        name: 'account-sign-out',
        component: SignOutView,
      },
      {
        path: 'sign-up',
        name: 'account-registration',
        component: RegisterAccountView,
      },
      {
        path: 'forgot-password',
        name: 'account-forgot-password',
        component: ForgotPasswordView,
      },
      {
        path: 'reset-password/:user/:token',
        name: 'account-reset-password',
        component: ResetPasswordView,
      },
      {
        path: 'change-email/:user/:token',
        name: 'account-change-email',
        component: ChangeEmailView,
      },
      {
        path: 'confirm-email/:user/:token',
        name: 'account-confirm-email',
        component: ConfirmEmailView,
      },
    ],
  },
  {
    path: '/error',
    name: 'error-page',
    component: ErrorView,
  },
  {
    path: '/access-denied',
    name: 'access-denied-page',
    component: AccessDeniedView,
  },
  {
    path: '/authentication-server-not-responding',
    name: 'authentication-server-not-responding',
    component: AuthenticationServerNotRespondingView,
  },
  {
    path: '*',
    redirect: { name: 'error-page', params: { errorMessageKey: 'not-found' } },
  },
];
